import React, { useRef } from "react";
import { formatTZDate } from "state/helpers";
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    Divider,
} from "@mui/material";
import { 
    Edit, 
    DeleteForever, 
    Visibility 
} from "@mui/icons-material";
import { LAYOUT_HEIGHT } from 'state/constants';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from "components/FlexBetween";
import FlexCenter from 'components/FlexCenter';


/* ── value renderer ─────────────────────────────────────────────────────── */
const SKIP_KEYS = new Set(["_id", "id", "__v"]);

// ISO date string: "2024-03-15T00:00:00.000Z" or "2024-03-15"
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;

const formatDate = (value) => {
    const time = formatTZDate(value, "UTC", "HH:mm:ss");
    if (!time) return null;
    const hasTime = time !== "00:00:00";
    return hasTime
        ? formatTZDate(value, "UTC", "dd/MM/yyyy HH:mm:ss")
        : formatTZDate(value, "UTC", "dd/MM/yyyy");
};

const renderValue = (value, depth = 0) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "boolean") return value ? "Yes" : "No";

    // Date object or ISO date string
    if (value instanceof Date) return formatDate(value);
    if (typeof value === "string" && ISO_DATE_RE.test(value)) {
        const formatted = formatDate(value);
        if (formatted) return formatted;
    }

    if (Array.isArray(value)) {
        if (value.length === 0) return null;
        // Array of primitives → join with comma
        if (value.every(v => v == null || typeof v !== "object")) {
            return value.filter(v => v != null).map(String).join(", ");
        }
        // Array of objects → each item on its own line
        if (depth >= 2) return `[${value.length} items]`;
        return value
            .map(item => item == null ? "" : renderValue(item, depth + 1))
            .filter(Boolean)
            .join("\n");
    }

    if (typeof value === "object") {
        const entries = Object.entries(value).filter(([k]) => !SKIP_KEYS.has(k));
        if (entries.length === 0) return null;
        if (depth >= 2) return "{...}";
        const sep = depth === 0 ? "\n" : " | ";
        return entries
            .map(([k, v]) => {
                const rendered = renderValue(v, depth + 1);
                if (/(^id$|_id$|Id$)/.test(k) || rendered === null) return null;
                return `${rendered}`;
            })
            .filter(Boolean)
            .join(sep);
    }

    return String(value);
};


/* ── CardList ─────────────────────────────────────────────────────────── */

/**
 * Props:
 *   rows           – data array
 *   primaryField   – field key for the bold card title
 *   secondaryField – optional subtitle field key
 *   fields         – [{ label, key, render?, cols? }] to display in the card body
 *                    cols: 1–4 (grid column span out of 4). Default 4 = full width.
 *                    cols:4 → 1 field/row, cols:2 → 2 fields/row, cols:1 → 4 fields/row
 *   isActiveField  – key used for the active/inactive chip + dimming.
 *                    Pass null to hide chip and never dim. Default: "isActive".
 *   statusChip     – { field, colorMap } — when provided, shows a status chip
 *                    instead of the isActive chip. colorMap: { value: MUI color }.
 *   canView        – boolean
 *   canEdit        – boolean
 *   canDelete      – boolean
 *   onView(id)     – called with item._id
 *   onEdit(id)     – called with item._id
 *   onDelete(id)   – called with item._id
 */
const CardList = ({
    rows = [],
    primaryField,
    secondaryField,
    fields = [],
    isActiveField,         // undefined → use "isActive"; null → no chip
    statusChip,            // { field, colorMap } — overrides isActive chip
    canView = false,
    canEdit = false,
    canDelete = false,
    onView,
    onEdit,
    onDelete,
    minWidth = 80,
}) => {
    const theme = useTheme();
    const scrollRef = useRef(null);

    const activeKey = isActiveField === undefined ? "isActive" : isActiveField;
    const showChip  = isActiveField !== null && !statusChip;

    if (!rows?.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">No data available</Typography>
            </FlexCenter>
        );
    }

    return (
        <Box
            ref={scrollRef}
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ height: '100%', overflowY: 'auto' }}
        >
            {rows?.map((item, index) => {
                const isActive = activeKey ? item[activeKey] !== false : true;
                const primaryValue = item[primaryField] ?? "-";

                const borderColor = isActive
                    ? theme.palette.neutral.deep
                    : theme.palette.neutral.light;

                return (
                    <Box
                        key={item._id || index}
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${borderColor}`,
                            backgroundColor: isActive
                                ? "background.paper"
                                : theme.palette.action.hover,
                            opacity: isActive ? 1 : 0.75,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": { boxShadow: isActive ? 3 : 1 },
                        }}
                    >
                        {/* Header */}
                        <FlexBetween>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                    label={index + 1}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        fontSize: "0.7rem",
                                        height: 20,
                                        "& .MuiChip-label": { px: 0.75 },
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    color={
                                        isActive
                                            ? theme.palette.primary.dark
                                            : theme.palette.text.disabled
                                    }
                                >
                                    {renderValue(primaryValue) ?? primaryValue}
                                </Typography>
                            </Box>
                            {statusChip && item[statusChip.field] ? (
                                <Chip
                                    label={item[statusChip.field].charAt(0).toUpperCase() + item[statusChip.field].slice(1)}
                                    size="small"
                                    color={statusChip.colorMap?.[item[statusChip.field]] || 'default'}
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                            ) : showChip ? (
                                <Chip
                                    label={isActive ? "Active" : "Inactive"}
                                    size="small"
                                    color={isActive ? "success" : "default"}
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                            ) : null}
                        </FlexBetween>

                        {/* Optional subtitle */}
                        {secondaryField && item[secondaryField] && (
                            <Typography
                                variant="caption"
                                color={isActive ? "text.secondary" : "text.disabled"}
                                display="block"
                                mt={0.25}
                                sx={{ pl: "calc(20px + 8px)" }}
                            >
                                {renderValue(item[secondaryField]) ?? String(item[secondaryField])}
                            </Typography>
                        )}

                        {/* Body fields */}
                        {fields.length > 0 && (
                            <>
                                <Divider sx={{ my: 0.75 }} />
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(4, 1fr)"
                                    gap={0.5}
                                    columnGap={1.5}
                                >
                                    {fields.map(({ label, key, render, cols = 4 }) => {
                                        const val = render ? render(item) : renderValue(item[key]);
                                        if (!val) return null;
                                        return (
                                            <Box
                                                key={key}
                                                display="flex"
                                                gap={0.75}
                                                sx={{ gridColumn: `span ${cols}` }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ minWidth, flexShrink: 0 }}
                                                >
                                                    {label}:
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color={isActive ? "text.primary" : "text.disabled"}
                                                    sx={{ wordBreak: "break-word", whiteSpace: "pre-line" }}
                                                >
                                                    {val}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </>
                        )}

                        {/* Actions */}
                        {(canView || canEdit || canDelete) && (
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                alignItems="center"
                                gap={2}
                            >
                                {canDelete && (
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            onClick={() => onDelete?.(item._id)}
                                        >
                                            <DeleteForever
                                                fontSize="small"
                                                sx={{ color: theme.palette.primary.dark }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {canEdit && (
                                    <Tooltip title="Edit">
                                        <IconButton
                                            size="small"
                                            onClick={() => onEdit?.(item._id)}
                                        >
                                            <Edit
                                                fontSize="small"
                                                sx={{ color: theme.palette.primary.dark }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {canView && (
                                    <Tooltip title="View">
                                        <IconButton
                                            size="small"
                                            onClick={() => onView?.(item._id)}
                                        >
                                            <Visibility
                                                fontSize="small"
                                                sx={{ color: theme.palette.primary.dark }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            })}

            <ScrollToTop scrollRef={scrollRef} />
        </Box>
    );
};

export default CardList;
